import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    FiAlertCircle,
    FiCalendar,
    FiChevronDown,
    FiDollarSign,
    FiEdit3,
    FiEye,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiX,
} from 'react-icons/fi';
import {
    ArrowDownAZ,
    ArrowDownNarrowWide,
    ArrowUpAZ,
    ArrowUpNarrowWide,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../common/BackButton';
import Toast from '../common/Toast';
import AssignmentContextMenu from '../schedule/components/AssignmentContextMenu';
import EditAssignment from '../assignments/EditAssignment';
import {
    getFinalPricingJobsPage,
    subscribeToFinalPricings,
    unsubscribeFromFinalPricings,
} from '../../lib/supabase/final-pricings';
import { isFinalPricingRequired, computeEstimationTotals, computeEffectivePricing, resolvePricingColumn } from '../../lib/supabase/finalPricingModes';
import FinalPricingDetails from './FinalPricingDetails';
import PricingColumnCell, { getColumnLabel } from './PricingColumnCell';
import PricingColumnsPopover, { sanitizeColumns } from './PricingColumnsPopover';

const VIEW_PERMISSION = 'view_finalpricing_page';
const EDIT_PERMISSION = 'edit_finalpricing_page';

const PAGE_LIMIT = 50;
const COMPACT_BREAKPOINT = 1024;
const DESKTOP_ROW_HEIGHT = 40;
const COMPACT_CARD_HEIGHT = 138;
const SCROLL_LOAD_THRESHOLD = 400;

const DEFAULT_FP_COLUMNS = [
    { source: 'all', grouping: 'per-department' },
    { source: 'estimation', grouping: 'total' },
    { source: 'all', grouping: 'total' },
];

const FIXED_COLUMN_WIDTHS = { index: 36, jobId: 140, customer: 170, address: 260, records: 90, action: 150 };
const PRICING_COLUMN_WIDTH = 160;

const getColumnWidths = (columnCount) => [
    FIXED_COLUMN_WIDTHS.index,
    FIXED_COLUMN_WIDTHS.jobId,
    FIXED_COLUMN_WIDTHS.customer,
    FIXED_COLUMN_WIDTHS.address,
    FIXED_COLUMN_WIDTHS.records,
    ...Array(columnCount).fill(PRICING_COLUMN_WIDTH),
    FIXED_COLUMN_WIDTHS.action,
];

const hasExactPermission = (userProfile, permission) =>
    Array.isArray(userProfile?.permissions) &&
    userProfile.permissions.includes(permission);

const loadPref = (key, fallback) => {
    try {
        const value = localStorage.getItem(key);
        return value !== null ? JSON.parse(value) : fallback;
    } catch {
        return fallback;
    }
};

const savePref = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Ignore localStorage errors.
    }
};

const addressToText = (address) => {
    if (!address) return '—';
    if (typeof address === 'string') return address;

    return [
        address.street,
        address.street_address,
        address.address_line_1,
        address.suburb,
        address.city,
        address.state,
        address.postcode,
    ].filter(Boolean).join(', ') || '—';
};

const pricingRecordCount = (job) =>
    (job.pricing_entries || []).filter(entry => !entry.is_archived).length;

// Every company's per-job pricing summary now goes through the same
// cascade (computeEffectivePricing). `estimationTotals`/`effectivePricing`
// are exposed as-is so JobRow/JobCard can resolve each of the user's
// configured columns against them without recomputing per column.
// `anyPriced`/`allPriced` (row coloring) and `total` (the canonical sort
// key) are both derived via resolvePricingColumn's `{ source: 'all' }`
// path — the exact same formula every "all"-source column on screen uses,
// so the row's coloring/sort can never disagree with what a user-visible
// "all" column shows for the same job.
const getJobPricingSummary = (job, requireFinalPricing) => {
    const estimationTotals = computeEstimationTotals(job.pricing_entries);
    const effectivePricing = computeEffectivePricing({ finalPricings: job.final_pricings, estimationTotals });

    const departmentsResolved = resolvePricingColumn({
        source: 'all', grouping: 'per-department', estimationTotals, effectivePricing, requireFinalPricing,
    });
    const anyPriced = departmentsResolved.departments.some(department => department.priced);
    const allPriced = departmentsResolved.departments.every(department => department.priced);

    const totalResolved = resolvePricingColumn({
        source: 'all', grouping: 'total', estimationTotals, effectivePricing, requireFinalPricing,
    });

    return {
        estimationTotals,
        effectivePricing,
        anyPriced,
        allPriced,
        total: totalResolved.total,
    };
};

const JobRow = React.memo(({ job, index, summary, canEdit, columns, requireFinalPricing, onOpen, onOpenAssignment, onEditAssignment }) => {
    const { allPriced: complete, anyPriced } = summary;
    const partial = !complete && anyPriced;
    const Icon = canEdit ? FiEdit3 : FiEye;

    return (
        <AssignmentContextMenu
            assignment={job}
            onOpenAssignment={onOpenAssignment}
            onEditAssignment={onEditAssignment}
            minimal
        >
        <tr
            className={`border-b border-gray-100 transition-colors ${complete
                ? 'bg-emerald-50/40 hover:bg-emerald-50/70'
                : partial
                    ? 'bg-amber-50/30 hover:bg-amber-50/60'
                    : 'hover:bg-gray-50'
                }`}
            style={{ height: DESKTOP_ROW_HEIGHT }}
        >
            <td className="px-1 py-1 text-center text-[10px] text-gray-300 font-mono">{index + 1}</td>
            <td className="px-2 py-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs font-semibold text-gray-900 truncate">{job.unique_id || '—'}</span>
                    {job.is_cancelled && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                    )}
                </div>
            </td>
            <td className="px-2 py-1">
                <span className="text-xs text-gray-700 truncate block" title={job.customer_name || ''}>
                    {job.customer_name || '—'}
                </span>
            </td>
            <td className="px-2 py-1">
                <span className="text-xs text-gray-500 truncate block" title={addressToText(job.address)}>
                    {addressToText(job.address)}
                </span>
            </td>
            <td className="px-2 py-1 text-center">
                <span className="inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[9px] font-semibold">
                    {pricingRecordCount(job)} rec
                </span>
            </td>
            {columns.map(column => (
                <td key={`${column.source}-${column.grouping}`} className={`px-2 py-1 ${column.grouping === 'total' ? 'text-right' : ''}`}>
                    <PricingColumnCell
                        column={column}
                        estimationTotals={summary.estimationTotals}
                        effectivePricing={summary.effectivePricing}
                        requireFinalPricing={requireFinalPricing}
                    />
                </td>
            ))}
            <td className="px-2 py-1">
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={() => onOpen(job.id)}
                        className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border transition-colors ${canEdit
                            ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        <Icon className="w-3 h-3" />
                        {canEdit ? (complete ? 'Edit' : 'Enter pricing') : 'View'}
                    </button>
                </div>
            </td>
        </tr>
        </AssignmentContextMenu>
    );
});

const JobCard = React.memo(({ job, summary, canEdit, columns, requireFinalPricing, onOpen }) => {
    const { allPriced: complete, anyPriced } = summary;
    const partial = !complete && anyPriced;
    const Icon = canEdit ? FiEdit3 : FiEye;

    return (
        <div
            className={`final-pricing-card mx-3 my-1.5 rounded-lg border p-3 flex flex-col gap-2 ${complete
                ? 'border-emerald-200 bg-emerald-50/40'
                : partial
                    ? 'border-amber-200 bg-amber-50/30'
                    : 'border-gray-200 bg-white'
                }`}
            style={{ minHeight: COMPACT_CARD_HEIGHT - 12 }}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-bold text-gray-900">{job.unique_id || '—'}</span>
                        {job.is_cancelled && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">CANCELLED</span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-600 truncate mt-0.5">{job.customer_name || '—'}</p>
                    <p className="text-[10px] text-gray-400 truncate">{addressToText(job.address)}</p>
                </div>
                <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[9px] font-semibold">
                    {pricingRecordCount(job)} rec
                </span>
            </div>

            <div className="flex flex-col gap-1.5 pt-1.5 border-t border-gray-100">
                {columns.map(column => (
                    <div key={`${column.source}-${column.grouping}`} className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-gray-400 font-medium shrink-0">{getColumnLabel(column)}</span>
                        <PricingColumnCell
                            column={column}
                            estimationTotals={summary.estimationTotals}
                            effectivePricing={summary.effectivePricing}
                            requireFinalPricing={requireFinalPricing}
                        />
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={() => onOpen(job.id)}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border transition-colors ${canEdit
                    ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
            >
                <Icon className="w-3 h-3" />
                {canEdit ? (complete ? 'Edit pricing' : 'Enter pricing') : 'View pricing'}
            </button>
        </div>
    );
});

const FilterBar = ({
    search,
    setSearch,
    pricingStatus,
    setPricingStatus,
    jobType,
    setJobType,
    cancelledStatus,
    setCancelledStatus,
    sortBy,
    setSortBy,
    columns,
    setColumns,
    loadedCount,
    total,
    hasMore,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };

        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const activeCount = [
        pricingStatus !== 'all',
        jobType !== 'all',
        cancelledStatus !== 'active',
    ].filter(Boolean).length;

    const sorts = [
        { value: 'newest', label: 'Newest first', icon: ArrowDownNarrowWide },
        { value: 'oldest', label: 'Oldest first', icon: ArrowUpNarrowWide },
        { value: 'alphabetical', label: 'Job ID A → Z', icon: ArrowDownAZ },
        { value: 'reverse_alphabetical', label: 'Job ID Z → A', icon: ArrowUpAZ },
        { value: 'highest_price', label: 'Highest final price', icon: ArrowDownNarrowWide },
        { value: 'lowest_price', label: 'Lowest final price', icon: ArrowUpNarrowWide },
    ];

    const countLabel = total !== null
        ? `${total.toLocaleString()} job${total !== 1 ? 's' : ''}`
        : `${loadedCount.toLocaleString()}+ job${loadedCount !== 1 ? 's' : ''}`;

    return (
        <div className="final-pricing-toolbar flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-b border-gray-100">
            <div className="relative min-w-[160px] max-w-sm flex-1">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search job ID, customer or address…"
                    className="w-full h-8 pl-8 pr-8 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <button
                type="button"
                onClick={() => setPricingStatus(pricingStatus === 'missing' ? 'all' : 'missing')}
                className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-all ${pricingStatus === 'missing'
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-amber-400'
                    }`}
            >
                Missing department pricing
            </button>

            <button
                type="button"
                onClick={() => setPricingStatus(pricingStatus === 'entered' ? 'all' : 'entered')}
                className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-all ${pricingStatus === 'entered'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'
                    }`}
            >
                All pricing entered
            </button>

            <PricingColumnsPopover
                columns={columns}
                onChange={setColumns}
                defaultColumns={DEFAULT_FP_COLUMNS}
            />

            <div className="relative" ref={ref}>
                <button
                    type="button"
                    onClick={() => setOpen(value => !value)}
                    className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium border rounded-md transition-all ${activeCount > 0 || open
                        ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-emerald-400'
                        }`}
                >
                    <FiFilter className="w-3 h-3" />
                    Filters
                    {activeCount > 0 && (
                        <span className="bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                            {activeCount}
                        </span>
                    )}
                    <FiChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                    <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-4">
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort</p>
                            <div className="space-y-1">
                                {sorts.map(({ value, label, icon: Icon }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setSortBy(value)}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${sortBy === value
                                            ? 'bg-emerald-50 text-emerald-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        <Icon className="w-3 h-3" />
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job type</p>
                            <select
                                value={jobType}
                                onChange={(event) => setJobType(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                <option value="all">All job types</option>
                                <option value="Design">Design</option>
                                <option value="Estimation">Estimation</option>
                                <option value="Design & Estimation">Design & Estimation</option>
                                <option value="Timber">Timber</option>
                            </select>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Cancelled jobs</p>
                            <select
                                value={cancelledStatus}
                                onChange={(event) => setCancelledStatus(event.target.value)}
                                className="w-full h-8 rounded-md border border-gray-200 px-2 text-xs"
                            >
                                <option value="active">Active only</option>
                                <option value="all">Active and cancelled</option>
                                <option value="cancelled">Cancelled only</option>
                            </select>
                        </div>

                        {activeCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setPricingStatus('all');
                                    setJobType('all');
                                    setCancelledStatus('active');
                                    setOpen(false);
                                }}
                                className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200 font-medium"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                )}
            </div>

            <span className="ml-auto text-xs text-gray-400">
                {countLabel}
                {hasMore && <span className="text-gray-300"> · scroll for more</span>}
            </span>
        </div>
    );
};

const PermissionDenied = () => (
    <div className="min-h-[55vh] flex items-center justify-center">
        <div className="max-w-md w-full rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-red-800">No permission</h1>
            <p className="text-sm text-red-700 mt-2">
                You do not have permission to view the Final Pricings page.
            </p>
        </div>
    </div>
);

const useIsCompact = () => {
    const [isCompact, setIsCompact] = useState(
        () => typeof window !== 'undefined' && window.innerWidth < COMPACT_BREAKPOINT
    );

    useEffect(() => {
        let frame = null;
        const handler = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
            });
        };

        window.addEventListener('resize', handler);
        return () => {
            window.removeEventListener('resize', handler);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return isCompact;
};

const FinalPricings = () => {
    const { company, userProfile } = useAuth();
    const { companySlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const canView = hasExactPermission(userProfile, VIEW_PERMISSION);
    const canEdit = hasExactPermission(userProfile, EDIT_PERMISSION);
    const requireFinalPricing = isFinalPricingRequired(company);

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [total, setTotal] = useState(null);

    const [search, setSearch] = useState(() => loadPref('fp_search', ''));
    const [debouncedSearch, setDebouncedSearch] = useState(() => loadPref('fp_search', ''));
    const [pricingStatus, setPricingStatus] = useState(loadPref('fp_status', 'all'));
    const [jobType, setJobType] = useState(loadPref('fp_job_type', 'all'));
    const [cancelledStatus, setCancelledStatus] = useState(loadPref('fp_cancelled', 'active'));
    const [sortBy, setSortBy] = useState(loadPref('fp_sort', 'newest'));
    const [columns, setColumns] = useState(() => sanitizeColumns(loadPref('fp_pricing_columns', DEFAULT_FP_COLUMNS), DEFAULT_FP_COLUMNS));

    const tableContainerRef = useRef(null);
    const offsetRef = useRef(0);
    const isCompact = useIsCompact();

    const [selectedFinalPricingJobId, setSelectedFinalPricingJobId] =
        useState(null);

    useEffect(() => savePref('fp_search', search), [search]);
    useEffect(() => savePref('fp_status', pricingStatus), [pricingStatus]);
    useEffect(() => savePref('fp_job_type', jobType), [jobType]);
    useEffect(() => savePref('fp_cancelled', cancelledStatus), [cancelledStatus]);
    useEffect(() => savePref('fp_sort', sortBy), [sortBy]);
    useEffect(() => savePref('fp_pricing_columns', columns), [columns]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const buildFilters = useCallback(() => ({
        search: debouncedSearch,
        jobType,
        cancelledStatus,
        pricingStatus,
        sortBy,
        requireFinalPricing,
    }), [debouncedSearch, jobType, cancelledStatus, pricingStatus, sortBy, requireFinalPricing]);

    const fetchFirstPage = useCallback(async () => {
        if (!canView || !company?.id) return;

        setLoading(true);
        try {
            const result = await getFinalPricingJobsPage(company.id, {
                offset: 0,
                limit: PAGE_LIMIT,
                ...buildFilters(),
            });

            setJobs(result.data);
            offsetRef.current = result.nextOffset;
            setHasMore(result.hasMore);
            setTotal(result.total);
        } catch (error) {
            console.error('Failed to load final pricing jobs:', error);
            Toast.error('Failed to load final pricing jobs', { position: 'bottom-right' });
        } finally {
            setLoading(false);
        }
    }, [canView, company?.id, buildFilters]);

    const loadMore = useCallback(async () => {
        if (!canView || !company?.id || loadingMore || !hasMore) return;

        setLoadingMore(true);
        try {
            const result = await getFinalPricingJobsPage(company.id, {
                offset: offsetRef.current,
                limit: PAGE_LIMIT,
                ...buildFilters(),
            });

            setJobs(previous => [...previous, ...result.data]);
            offsetRef.current = result.nextOffset;
            setHasMore(result.hasMore);
            if (result.total !== null) setTotal(result.total);
        } catch (error) {
            console.error('Failed to load more final pricing jobs:', error);
            Toast.error('Failed to load more jobs', { position: 'bottom-right' });
        } finally {
            setLoadingMore(false);
        }
    }, [canView, company?.id, loadingMore, hasMore, buildFilters]);

    useEffect(() => {
        fetchFirstPage();
    }, [fetchFirstPage]);

    useEffect(() => {
        if (!canView || !company?.id) return undefined;

        const channel = subscribeToFinalPricings(company.id, (payload) => {
            const jobId = payload?.new?.job_id || payload?.old?.job_id;
            const department = payload?.new?.department || payload?.old?.department;
            if (!jobId || !department) return;

            setJobs(previous => previous.map(job => {
                if (job.id !== jobId) return job;

                return {
                    ...job,
                    final_pricings: {
                        ...(job.final_pricings || {}),
                        [department]: payload.eventType === 'DELETE' ? null : payload.new,
                    },
                };
            }));
        });

        return () => unsubscribeFromFinalPricings(channel);
    }, [canView, company?.id]);

    useEffect(() => {
        const el = tableContainerRef.current;
        if (!el) return undefined;

        const handler = () => {
            if (loadingMore || !hasMore) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD) {
                loadMore();
            }
        };

        el.addEventListener('scroll', handler, { passive: true });
        return () => el.removeEventListener('scroll', handler);
    }, [hasMore, loadingMore, loadMore]);

    // Each job's pricing summary (which, in estimation-based modes, calls
    // computeEstimationTotals) is computed exactly once per job here and
    // threaded down as a prop, rather than having JobRow/JobCard re-derive it
    // per rendered row. Price sorts have no backing DB column (a sum across
    // up to 3 related rows), so they're applied client-side across whatever
    // has been loaded so far, reusing these same summaries; every other sort
    // is applied server-side in the query.
    const displayJobs = useMemo(() => {
        const withSummaries = jobs.map(job => ({ job, summary: getJobPricingSummary(job, requireFinalPricing) }));

        if (sortBy !== 'highest_price' && sortBy !== 'lowest_price') return withSummaries;

        withSummaries.sort((a, b) => {
            const aTotal = a.summary.total;
            const bTotal = b.summary.total;

            if (aTotal === null) return 1;
            if (bTotal === null) return -1;

            return sortBy === 'highest_price' ? bTotal - aTotal : aTotal - bTotal;
        });

        return withSummaries;
    }, [jobs, sortBy, requireFinalPricing]);

    const columnWidths = useMemo(() => getColumnWidths(columns.length), [columns.length]);
    const tableMinWidth = useMemo(() => columnWidths.reduce((sum, width) => sum + width, 0), [columnWidths]);

    const openJob = useCallback((jobId) => {
        setSelectedFinalPricingJobId(jobId);
    }, []);

    const [editAssignmentJobId, setEditAssignmentJobId] = useState(null);

    const openAssignment = useCallback((jobId) => {
        window.open(`/${companySlug}/assignment/${jobId}`, '_blank');
    }, [companySlug]);

    const editAssignment = useCallback((jobId) => {
        setEditAssignmentJobId(jobId);
    }, []);

    const virtualizer = useVirtualizer({
        count: displayJobs.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: useCallback(
            () => (isCompact ? COMPACT_CARD_HEIGHT : DESKTOP_ROW_HEIGHT),
            [isCompact]
        ),
        getItemKey: index => displayJobs[index].job.id,
        overscan: 8,
    });

    useEffect(() => {
        virtualizer.measure();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCompact]);

    const virtualRows = virtualizer.getVirtualItems();
    const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length
        ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
        : 0;

    if (!canView) return <PermissionDenied />;

    return (
        <>
            <div className="final-pricings-page flex flex-col gap-6 pb-10">
                <div className="flex flex-col gap-3">
                    <BackButton
                        text="Back"
                        variant="ghost"
                        size="sm"
                        iconType="chevron"
                        className="text-blue-600 hover:text-blue-800 self-start"
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg">
                                <FiDollarSign className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Final Pricings</h1>
                                <p className="text-gray-500 text-sm mt-0.5">
                                    Review pricing records and manage separate Roof, Wall and Floor final prices.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => navigate(`/${companySlug}/calendar/final-pricing`, { state: { from: location.pathname } })}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                            >
                                <FiCalendar className="w-4 h-4" />
                                Calendar view
                            </button>

                            <button
                                type="button"
                                onClick={fetchFirstPage}
                                disabled={loading}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            >
                                <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>

                    {!canEdit && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                            You have view-only access. Final pricing changes are disabled.
                        </div>
                    )}
                </div>

                <div className="final-pricing-panel flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                    <FilterBar
                        search={search}
                        setSearch={setSearch}
                        pricingStatus={pricingStatus}
                        setPricingStatus={setPricingStatus}
                        jobType={jobType}
                        setJobType={setJobType}
                        cancelledStatus={cancelledStatus}
                        setCancelledStatus={setCancelledStatus}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        columns={columns}
                        setColumns={setColumns}
                        loadedCount={displayJobs.length}
                        total={total}
                        hasMore={hasMore}
                    />

                    <div ref={tableContainerRef} className="final-pricing-scroll overflow-auto" style={{ height: '75vh' }}>
                        {loading ? (
                            <div className="h-40 flex items-center justify-center">
                                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                <span className="ml-3 text-sm text-gray-500">Loading…</span>
                            </div>
                        ) : displayJobs.length === 0 ? (
                            <div className="py-16 text-center">
                                <FiFilter className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                                <p className="text-sm font-medium text-gray-500">No jobs match the current filters</p>
                            </div>
                        ) : isCompact ? (
                            <>
                                {paddingTop > 0 && <div style={{ height: paddingTop }} />}
                                {virtualRows.map(virtualRow => (
                                    <div key={displayJobs[virtualRow.index].job.id} data-index={virtualRow.index} ref={virtualizer.measureElement} style={{ display: 'flow-root' }}>
                                    <JobCard
                                        job={displayJobs[virtualRow.index].job}
                                        summary={displayJobs[virtualRow.index].summary}
                                        canEdit={canEdit}
                                        columns={columns}
                                        requireFinalPricing={requireFinalPricing}
                                        onOpen={openJob}
                                    />
                                    </div>
                                ))}
                                {paddingBottom > 0 && <div style={{ height: paddingBottom }} />}
                            </>
                        ) : (
                            <table className="w-full text-xs border-collapse" style={{ minWidth: tableMinWidth, tableLayout: 'fixed' }}>
                                <colgroup>
                                    {columnWidths.map((width, index) => (
                                        <col key={index} style={{ width }} />
                                    ))}
                                </colgroup>
                                <thead className="sticky top-0 z-20 bg-gray-50">
                                    <tr>
                                        <th className="px-1 py-2 text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-200">#</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Job ID</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Customer</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Address</th>
                                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Records</th>
                                        {columns.map(column => (
                                            <th key={`${column.source}-${column.grouping}`} className={`px-2 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 ${column.grouping === 'total' ? 'text-right' : 'text-left'}`}>
                                                {getColumnLabel(column)}
                                            </th>
                                        ))}
                                        <th className="px-2 py-2 text-center text-[10px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paddingTop > 0 && (
                                        <tr><td colSpan={columnWidths.length} style={{ height: paddingTop }} /></tr>
                                    )}

                                    {virtualRows.map(virtualRow => (
                                        <JobRow
                                            key={displayJobs[virtualRow.index].job.id}
                                            job={displayJobs[virtualRow.index].job}
                                            summary={displayJobs[virtualRow.index].summary}
                                            index={virtualRow.index}
                                            canEdit={canEdit}
                                            columns={columns}
                                            requireFinalPricing={requireFinalPricing}
                                            onOpen={openJob}
                                            onOpenAssignment={openAssignment}
                                            onEditAssignment={editAssignment}
                                        />
                                    ))}

                                    {paddingBottom > 0 && (
                                        <tr><td colSpan={columnWidths.length} style={{ height: paddingBottom }} /></tr>
                                    )}
                                </tbody>
                            </table>
                        )}

                        {loadingMore && (
                            <div className="flex items-center justify-center py-4 gap-2">
                                <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-gray-500">Loading more…</span>
                            </div>
                        )}

                        {!loading && !loadingMore && !hasMore && displayJobs.length > 0 && (
                            <p className="text-center text-[11px] text-gray-400 py-4">
                                All {(total ?? displayJobs.length).toLocaleString()} jobs loaded
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {selectedFinalPricingJobId && (
                <FinalPricingDetails
                    modalJobId={selectedFinalPricingJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => setSelectedFinalPricingJobId(null)}
                    onSaved={({
                        jobId,
                        department,
                        finalPricing,
                    }) => {
                        setJobs(previous =>
                            previous.map(job =>
                                job.id === jobId
                                    ? {
                                        ...job,

                                        final_pricings: {
                                            ...(job.final_pricings || {}),
                                            [department]: finalPricing,
                                        },
                                    }
                                    : job
                            )
                        );
                    }}
                />
            )}

            {editAssignmentJobId && (
                <EditAssignment
                    modalJobId={editAssignmentJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => setEditAssignmentJobId(null)}
                />
            )}
        </>
    );
};

export default FinalPricings;
